// buildInfo.ts — czym DOKŁADNIE jest artefakt, który właśnie działa w przeglądarce.
//
// POWÓD ISTNIENIA
// ---------------
// Do R5-15-004 tożsamość wdrożonego artefaktu dało się ustalić wyłącznie przez reprodukcję:
// zbuduj to samo lokalnie, policz sumy kontrolne, porównaj z tym, co serwuje serwer. To działa,
// ale wymaga dostępu do kodu, tej samej wersji zależności i cierpliwości — a odpowiedź na pytanie
// „co tam właściwie stoi?” powinna zająć pięć sekund, nie pół godziny.
//
// Dlatego SHA commita, czas builda i nazwa środowiska są wstrzykiwane do artefaktu w chwili
// budowania (vite.config.ts) i pokazywane w przestrzeni SYSTEM oraz wystawiane jako
// `<base>/build-info.json` — czyli dają się sprawdzić także bez przeglądarki, jednym curl-em.
//
// CZEGO TU NIE MA I BYĆ NIE MOŻE
// -------------------------------
// Żadnych sekretów, żadnych poświadczeń repozytorium, żadnych adresów wewnętrznych. Ten obiekt
// trafia do publicznie serwowanego pliku JS — wszystko, co w nim umieścisz, jest jawne.

export interface BuildInfo {
  /** Wersja z package.json. */
  appVersion: string;
  /** Pełny SHA commita, z którego powstał build. `unknown`, gdy build nie miał dostępu do gita. */
  gitSha: string;
  /**
   * Czy drzewo robocze miało niezacommitowane zmiany w chwili budowania.
   * To jest ważniejsze niż wygląda: build z brudnego drzewa NIE jest jednoznacznie
   * powiązywalny z SHA, więc `MATCH_SOURCE_TO_DEPLOY` musi wtedy paść.
   */
  gitDirty: boolean;
  /** Gałąź źródłowa, jeśli dało się ją ustalić. */
  gitBranch: string;
  /** Czas zakończenia builda, ISO 8601 UTC. */
  buildTime: string;
  /** Nazwa środowiska, pod które build był konfigurowany. */
  environment: string;
  /** Prefiks ścieżki, pod którym artefakt ma być serwowany. */
  base: string;
  /** Adresy backendów wpieczone w bundle. */
  api: { cc: string };
}

declare const __CC_BUILD_INFO__: BuildInfo | undefined;

const NIEZNANY: BuildInfo = {
  appVersion: 'unknown',
  gitSha: 'unknown',
  gitDirty: false,
  gitBranch: 'unknown',
  buildTime: 'unknown',
  environment: 'unknown',
  base: '/',
  api: { cc: '(to samo pochodzenie)' },
};

/**
 * Metadane builda. Pod testami jednostkowymi stała bywa niezdefiniowana — wtedy zwracamy
 * jawne `unknown` zamiast wywracać aplikację. Brak metadanych to informacja, nie awaria.
 */
export const buildInfo: BuildInfo =
  typeof __CC_BUILD_INFO__ !== 'undefined' && __CC_BUILD_INFO__ ? __CC_BUILD_INFO__ : NIEZNANY;

/** Skrócony SHA do wyświetlenia. Pełny zostaje dostępny w `buildInfo.gitSha`. */
export function shortSha(sha: string): string {
  return sha === 'unknown' ? sha : sha.slice(0, 12);
}

/**
 * Jednozdaniowy opis artefaktu do nagłówków raportów i zrzutów ekranu.
 * Świadomie zawiera znacznik brudnego drzewa — to najczęstsza przyczyna sytuacji
 * „wdrożyliśmy coś, czego nie ma w repozytorium”.
 */
export function describeBuild(info: BuildInfo = buildInfo): string {
  const sha = shortSha(info.gitSha);
  const brud = info.gitDirty ? ' (drzewo brudne)' : '';
  return `v${info.appVersion} · ${sha}${brud} · ${info.buildTime} · ${info.environment}`;
}
